import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Coffee, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { dailyFocusBlocksApi, workItemsApi } from '@/api/services';
import type { DailyFocusBlock, WorkItem } from '@/types/models';
import { useAuth } from '@/auth/AuthProvider';

// Window-level event bus used by anywhere that wants to open the
// full-screen focus session — Dashboard's "Begin focus" button, the
// floating HUD's expand control, the Plan page's per-block "Begin",
// etc. Decoupled this way so consumers don't need a context.
export const FOCUS_SESSION_OPEN_EVENT = 'cadence:focus-session:open';

export function openFocusSession() {
  window.dispatchEvent(new CustomEvent(FOCUS_SESSION_OPEN_EVENT));
}

const BREAK_DURATIONS = [5, 10, 15, 20, 30];

function minuteOfDay(hhmmss: string): number {
  const [h = '0', m = '0'] = hhmmss.split(':');
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

function currentMinuteOfDay(): number {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

function formatHM(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = Math.floor(mins % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function tomorrowStr(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// FocusSessionFull — distraction-free full-screen overlay for the
// active focus block. Always-dark by design; the workspace theme can
// be light but the focus session is its own cinematic moment. Renders
// the 248 px countdown ring, the tasks for the block (complete /
// defer), and a break timer with chime alert.
//
// Opens via `openFocusSession()` from anywhere. Closes via the End
// focus button or Escape key.
export function FocusSessionFull() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [activeBlock, setActiveBlock] = useState<DailyFocusBlock | null>(null);
  const [items, setItems] = useState<WorkItem[]>([]);
  const [now, setNow] = useState<number>(Date.now());
  const [breakEndsAt, setBreakEndsAt] = useState<number | null>(null);
  const [breakTotal, setBreakTotal] = useState(0);
  const [showBreakChips, setShowBreakChips] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Open via the global event bus.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(FOCUS_SESSION_OPEN_EVENT, handler as EventListener);
    return () => window.removeEventListener(FOCUS_SESSION_OPEN_EVENT, handler as EventListener);
  }, []);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Tick every second so countdowns animate.
  useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  // Lock body scroll while the overlay is up.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Load the active block + its planned items when the overlay opens.
  const loadActive = useCallback(async () => {
    if (!user) return;
    try {
      const today = new Date();
      const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const blocks = await dailyFocusBlocksApi.getByDate(todayKey);
      const cur = currentMinuteOfDay();
      const block = blocks.find(b => {
        const s = minuteOfDay(b.startTime);
        const e = minuteOfDay(b.endTime);
        return cur >= s && cur < e;
      }) ?? blocks.find(b => minuteOfDay(b.startTime) >= cur)
         ?? blocks[0]
         ?? null;
      setActiveBlock(block);
      if (block) {
        // Prefer the block's own linked work items — this is what the
        // executive (or the EA) explicitly scoped for this block when
        // they planned the day. Map LinkedWorkItemSummary into the
        // WorkItem shape the renderer below expects (only the fields
        // we actually read; rest are typed as optional in WorkItem so
        // a partial is acceptable for our local display state).
        const linked = block.linkedWorkItems ?? [];
        if (linked.length > 0) {
          setItems(linked.slice(0, 6).map(l => ({
            id: l.id,
            title: l.title,
            type: l.type,
            priority: l.priority,
            status: l.status,
            estimatedMinutes: l.estimatedMinutes ?? 0,
          }) as unknown as WorkItem));
        } else {
          // Legacy fallback — focus blocks created before the
          // FocusBlock ↔ WorkItem link existed have no linkedWorkItems.
          // Fall back to today's open items just for those blocks so
          // the session isn't presented as empty.
          const wis = await workItemsApi.getAll();
          const todayKey = (() => {
            const d = new Date();
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          })();
          const planned = wis.filter(w =>
            w.status !== 'Done' && w.status !== 'Cancelled' &&
            ((w.targetDate && w.targetDate.startsWith(todayKey)) || w.status === 'InProgress'),
          ).slice(0, 6);
          setItems(planned);
        }
      }
    } catch { /* silent — leave block null, render empty state */ }
  }, [user]);

  useEffect(() => { if (open) void loadActive(); }, [open, loadActive]);

  // Break-end chime.
  useEffect(() => {
    if (!breakEndsAt) return;
    if (now >= breakEndsAt) {
      try { audioRef.current?.play().catch(() => {}); } catch {}
      setBreakEndsAt(null);
    }
  }, [now, breakEndsAt]);

  const close = () => setOpen(false);

  const onComplete = async (item: WorkItem) => {
    setItems(prev => prev.filter(i => i.id !== item.id));
    try { await workItemsApi.update(item.id, { status: 'Done' }); }
    catch { setItems(prev => [...prev, item]); }
  };
  const onDefer = async (item: WorkItem) => {
    setItems(prev => prev.filter(i => i.id !== item.id));
    try { await workItemsApi.update(item.id, { targetDate: tomorrowStr() }); }
    catch { setItems(prev => [...prev, item]); }
  };

  // Countdown maths. Read seconds-since-midnight from `now` so the
  // visible countdown ticks every second — the previous version
  // computed `cur` from getHours() * 60 + getMinutes(), which is
  // minute-resolution and meant the ring sat still for 60 seconds at
  // a time. Bug spotted on first real focus block.
  const blockGeom = useMemo(() => {
    if (!activeBlock) return null;
    const s = minuteOfDay(activeBlock.startTime);
    const e = minuteOfDay(activeBlock.endTime);
    const nowDate = new Date(now);
    const curSeconds = nowDate.getHours() * 3600 + nowDate.getMinutes() * 60 + nowDate.getSeconds();
    const startSeconds = s * 60;
    const total = Math.max(1, e - s) * 60; // seconds
    const elapsed = Math.max(0, Math.min(total, curSeconds - startSeconds));
    const remaining = total - elapsed;
    return { s, e, total, remaining, elapsed };
  }, [activeBlock, now]);

  const breakRemaining = breakEndsAt ? Math.max(0, breakEndsAt - now) : 0;
  const onBreak = breakEndsAt !== null;

  const displaySecs = onBreak
    ? Math.floor(breakRemaining / 1000)
    : blockGeom?.remaining ?? 0;
  const dispMm = Math.floor(displaySecs / 60);
  const dispSs = displaySecs % 60;

  const frac = onBreak
    ? (breakTotal > 0 ? 1 - breakRemaining / breakTotal : 0)
    : blockGeom ? blockGeom.elapsed / blockGeom.total : 0;

  // 248 px ring.
  const R = 110;
  const C = 2 * Math.PI * R;
  const offset = C * (1 - frac);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950 text-slate-100"
      role="dialog"
      aria-modal
      aria-label="Focus session"
    >
      <audio ref={audioRef} src="/sounds/chime.mp3" preload="auto" />
      {/* Ambient backdrop — a soft blue/violet glow without taking
          attention from the ring. */}
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            'radial-gradient(circle at 50% 30%, rgba(125, 177, 255, 0.18), transparent 55%), radial-gradient(circle at 70% 80%, rgba(179, 157, 255, 0.12), transparent 60%)',
        }}
      />

      <button
        onClick={close}
        className="absolute top-5 right-5 inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-sm text-slate-300 backdrop-blur hover:bg-white/10"
      >
        <X className="h-4 w-4" />
        End focus
      </button>

      <div className="relative flex w-full max-w-2xl flex-col items-center px-6 py-12">
        <p className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
          In focus{blockGeom ? ` · ${formatHM(blockGeom.s)} to ${formatHM(blockGeom.e)}` : ''}
        </p>
        <h1 className="mt-2 text-center font-serif text-3xl font-light leading-tight tracking-tight">
          {activeBlock?.label || 'Deep work'}
        </h1>

        <div className="relative my-8 h-[248px] w-[248px]">
          <svg width="248" height="248" viewBox="0 0 248 248" aria-hidden>
            <circle
              cx="124"
              cy="124"
              r={R}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="6"
            />
            <circle
              cx="124"
              cy="124"
              r={R}
              fill="none"
              stroke="hsl(220, 90%, 70%)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={offset}
              transform="rotate(-90 124 124)"
              style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(0.2, 0, 0, 1)' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-mono text-5xl font-semibold tracking-tight tabular-nums">
              {String(dispMm).padStart(2, '0')}:{String(dispSs).padStart(2, '0')}
            </span>
            <span className="mt-1 text-xs text-slate-400">
              {onBreak ? 'on a break' : displaySecs === 0 ? 'wrapping up' : 'remaining'}
            </span>
          </div>
        </div>

        {!onBreak && (
          <div className="w-full max-w-md space-y-2">
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">
              Working on
            </p>
            {items.length === 0 ? (
              <p className="rounded-lg border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
                All clear. Enjoy the focus.
              </p>
            ) : (
              items.map(item => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 backdrop-blur"
                >
                  <button
                    onClick={() => onComplete(item)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 text-slate-300 hover:bg-emerald-400/20 hover:text-emerald-200"
                    title="Mark done"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.title}</p>
                    <p className="text-xs text-slate-400">{item.type}</p>
                  </div>
                  <button
                    onClick={() => onDefer(item)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 text-slate-300 hover:bg-white/10"
                    title="Defer to tomorrow"
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        <div className="mt-6">
          {onBreak ? (
            <Button
              variant="outline"
              onClick={() => setBreakEndsAt(null)}
              className="border-white/20 bg-white/5 text-slate-100 hover:bg-white/10"
            >
              End break
            </Button>
          ) : showBreakChips ? (
            <div className="flex flex-wrap items-center justify-center gap-2">
              {BREAK_DURATIONS.map(m => (
                <button
                  key={m}
                  onClick={() => {
                    const totalMs = m * 60 * 1000;
                    setBreakTotal(totalMs);
                    setBreakEndsAt(Date.now() + totalMs);
                    setShowBreakChips(false);
                  }}
                  className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10"
                >
                  {m}m
                </button>
              ))}
              <button
                onClick={() => setShowBreakChips(false)}
                className="rounded-full px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setShowBreakChips(true)}
              className="gap-2 border-white/20 bg-white/5 text-slate-100 hover:bg-white/10"
            >
              <Coffee className="h-4 w-4" />
              Take a break
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FocusSessionFull;
